// 设备运行详情（/safety-device/:name）
// 生产安全页 10 台设备的下级页面：左侧监控视频窗（时间戳 + Camera 标签）+ 右侧运行状态面板
// 由 2223 移植：2221 舞台 7680×2160，横向/字号 ×2；视频窗按 2160 纵向预算收敛为宽幅画面
<template>
  <div class="dv-page">
    <div class="dv-viewport" ref="viewport">
      <div class="dv-scale-box" ref="box">
        <div class="dv-stage" ref="stage">
          <!-- 左上设备名 -->
          <div class="dv-title-row">
            <h1 class="dv-title">{{ device.name }}<i class="dv-title-sub">{{ $C('safetyDevice','titleSub','设备运行实况') }}</i></h1>
            <button class="dv-back" type="button" @click="goBack">
              <span class="dv-back-arrow">←</span> {{ $C('safetyDevice','backText','返回设备列表') }}
            </button>
          </div>

          <!-- 主体：左视频 + 右状态 -->
          <div class="dv-body">
            <!-- 监控视频窗 -->
            <div class="dv-screen">
              <video
                class="dv-video"
                :src="videoSrc"
                autoplay
                muted
                loop
                playsinline
              ></video>
              <span class="dv-ts">{{ nowText }}</span>
              <span class="dv-cam">{{ device.camera }}</span>
              <span class="dv-live"><i></i>{{ $C('safetyDevice','liveText','实时监控') }}</span>
            </div>

            <!-- 运行状态面板 -->
            <aside class="dv-panel">
              <div class="dv-row dv-row-state">
                <span class="dv-ico">
                  <svg viewBox="0 0 24 24" fill="none"><path d="M8 5.5v13l10-6.5-10-6.5z" fill="currentColor"/></svg>
                </span>
                <span class="dv-row-label">{{ $C('safetyDevice','stateLabel','设备开') }}</span>
                <span class="dv-row-value dv-state">
                  {{ $C('safetyDevice','stateValue','运行正常') }}
                  <i class="dv-dot"></i>
                </span>
              </div>
              <div class="dv-row" v-for="m in live" :key="m.label">
                <span class="dv-ico">
                  <svg v-if="m.kind === 'gauge'" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 12l4.2-3.4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <circle cx="12" cy="12" r="1.4" fill="currentColor"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="8.5" stroke="currentColor" stroke-width="1.8"/>
                    <path d="M12 7v5h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </span>
                <span class="dv-row-label">{{ m.label }}<i v-if="m.unit" class="dv-unit">（{{ m.unit }}）</i></span>
                <span class="dv-row-value">{{ m.disp }}</span>
              </div>
              <p class="dv-desc">{{ device.desc }}</p>
            </aside>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { SAFETY_DEVICES } from '@/data/productionSafety';
import { mediaUrl } from '@/cms/config';

export default {
  name: 'safetyDevice',
  data() {
    return {
      now: new Date(),
      timer: null,
      jitter: null,
      live: [],
    };
  },
  computed: {
    device() {
      let name = this.$route.params.name || '';
      try { name = decodeURIComponent(name); } catch (e) { /* 保持原值 */ }
      const list = this.$CList('safetyDevice', 'devices', SAFETY_DEVICES);
      return list.find(d => d.name === name) || list[0];
    },
    videoSrc() {
      // 后台「设备运行详情」分片可逐设备配置监控视频（deviceVideos：按设备 key 填写，留空则用全局默认）；
      // 再回退到全局 videoUrl，最后回退到页面内置占位片源。
      const map = this.$C('safetyDevice', 'deviceVideos', [])
      const row = (map || []).find(x => x && x.key === this.device.key)
      const v = (row && row.video) || this.$C('safetyDevice', 'videoUrl', process.env.BASE_URL + 'assets/ai/ai.webm')
      return /^(\/)?media\//.test(v) ? mediaUrl(v) : v
    },
    nowText() {
      const d = this.now;
      const wd = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][d.getDay()];
      const p = v => String(v).padStart(2, '0');
      return `${d.getFullYear()}年${p(d.getMonth() + 1)}月${p(d.getDate())}日  ${wd}  ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
  },
  watch: {
    device: {
      immediate: true,
      handler() { this.resetLive(); },
    },
  },
  mounted() {
    this.timer = setInterval(() => { this.now = new Date(); }, 1000);
    this.jitter = setInterval(() => this.tick(), 2600);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearInterval(this.jitter);
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.replace('/safety-flow');
    },
    resetLive() {
      this.live = this.device.metrics.map(m => ({
        ...m,
        cur: m.value,
        disp: m.value.toFixed(m.digits),
      }));
    },
    tick() {
      // 数值微动模拟实时采集（范围受 min/max 约束），接入真实接口后由 /api/safety/devices/:key 推送
      this.live = this.live.map(m => {
        if (m.max <= m.min) return m;
        const span = (m.max - m.min) * 0.08;
        let cur = m.cur + (Math.random() - 0.5) * span;
        cur = Math.min(m.max, Math.max(m.min, cur));
        return { ...m, cur, disp: cur.toFixed(m.digits) };
      });
    },
  },
};
</script>

<style lang="less" scoped>
.dv-page {
  position: fixed;
  inset: 0;
  z-index: 1010; /* 覆盖 safety-flow 全屏层 */
  background:
    radial-gradient(1100px 560px at 30% 100%, rgba(16, 60, 130, 0.4), rgba(0, 0, 0, 0) 70%),
    linear-gradient(165deg, #02091a 0%, #051228 55%, #02091a 100%);
  overflow: hidden;
  font-family: 'Microsoft YaHei', 'PingFang SC', sans-serif;
}
.dv-viewport {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.dv-scale-box { position: relative; }
.dv-stage {
  width: 7680px;
  height: 2160px;
  transform-origin: top left;
  padding: 130px 180px 110px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 70px;
}

.dv-title-row {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.dv-title {
  font-size: 112px;
  font-weight: 700;
  letter-spacing: 12px;
  color: #fff;
  text-shadow: 0 0 36px rgba(80, 200, 255, 0.7);
  .dv-title-sub {
    font-style: normal;
    font-size: 64px;
    font-weight: 400;
    letter-spacing: 8px;
    color: rgba(150, 205, 255, 0.85);
    margin-left: 60px;
  }
}
.dv-back {
  background: rgba(0, 229, 255, 0.12);
  border: 2px solid rgba(0, 229, 255, 0.55);
  color: #00E5FF;
  font-size: 60px;
  letter-spacing: 6px;
  padding: 28px 72px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover { background: rgba(0, 229, 255, 0.25); box-shadow: 0 0 36px rgba(0, 229, 255, 0.5); }
}
.dv-back-arrow { margin-right: 12px; }

.dv-body {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 100px;
}

/* ---------- 监控视频窗 ---------- */
.dv-screen {
  flex: none;
  width: 3600px;
  height: 1600px;
  position: relative;
  border: 5px solid rgba(90, 170, 255, 0.85);
  border-radius: 52px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 0 80px rgba(40, 130, 255, 0.35), inset 0 0 60px rgba(0, 20, 60, 0.6);
}
.dv-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.dv-ts {
  position: absolute;
  top: 68px;
  left: 80px;
  font-size: 68px;
  letter-spacing: 4px;
  color: #fff;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9);
  font-family: 'Consolas', 'Microsoft YaHei', monospace;
}
.dv-cam {
  position: absolute;
  right: 88px;
  bottom: 72px;
  font-size: 72px;
  letter-spacing: 6px;
  color: #fff;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9);
}
.dv-live {
  position: absolute;
  top: 68px;
  right: 88px;
  display: flex;
  align-items: center;
  gap: 24px;
  font-size: 56px;
  letter-spacing: 6px;
  color: #ff6b5e;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.9);
  i {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #ff5348;
    box-shadow: 0 0 24px rgba(255, 90, 70, 0.95);
    animation: dv-blink 1.4s ease-in-out infinite;
  }
}
@keyframes dv-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }

/* ---------- 右侧状态面板 ---------- */
.dv-panel {
  flex: 1;
  min-width: 0;
  height: 1600px;
  border: 6px solid rgba(70, 150, 255, 0.9);
  border-radius: 88px;
  background: rgba(6, 20, 46, 0.72);
  box-shadow: 0 0 68px rgba(50, 130, 255, 0.4), inset 0 0 80px rgba(20, 70, 160, 0.25);
  padding: 120px 100px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 100px;
}
.dv-row {
  display: flex;
  align-items: center;
  gap: 60px;
}
.dv-ico {
  flex: none;
  width: 176px;
  height: 176px;
  border-radius: 50%;
  border: 5px solid #fff;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { width: 104px; height: 104px; }
}
.dv-row-label {
  flex: 1;
  min-width: 0;
  font-size: 84px;
  font-weight: 600;
  letter-spacing: 6px;
  color: #fff;
  white-space: nowrap;
  .dv-unit {
    font-style: normal;
    font-size: 64px;
    font-weight: 400;
    color: rgba(200, 225, 255, 0.85);
  }
}
.dv-row-value {
  flex: none;
  font-size: 104px;
  font-weight: 700;
  color: #fff;
  font-family: 'DIN Alternate', 'Arial', sans-serif;
}
.dv-state {
  display: flex;
  align-items: center;
  gap: 40px;
  font-size: 84px;
  font-weight: 600;
  font-family: inherit;
  letter-spacing: 6px;
}
.dv-dot {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: radial-gradient(circle at 40% 35%, #7dffb0, #23c86a 70%);
  box-shadow: 0 0 32px rgba(60, 220, 130, 0.95);
}
.dv-desc {
  margin-top: 40px;
  font-size: 60px;
  line-height: 104px;
  letter-spacing: 4px;
  color: rgba(170, 210, 250, 0.9);
  border-top: 2px solid rgba(90, 160, 255, 0.3);
  padding-top: 60px;
}
</style>
